import { useEffect, useMemo } from "react";
import {
  IconAward,
  IconInbox,
  IconRocket,
  IconStar,
} from "@tabler/icons-react";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import { analytics } from "../../analytics/analyticsEvents";
import { clients, contracts, getClientById, getContractById } from "../../content";
import { useLocalize } from "../../content/useLocalize";
import { isUrgentActive } from "../../engine/contractOffers";
import { getNextUnlockClient, getRecommendedContract } from "../../engine/progression";
import { useGameStore } from "../../state/store";
import type { ContractId } from "../../types";
import { AchievementsPanel } from "../achievements/AchievementsPanel";
import { Button } from "../common/Button";
import { EmptyState } from "../common/EmptyState";
import { GradeSeal } from "../common/GradeSeal";
import { Panel } from "../common/Panel";
import { ProgressBar } from "../common/ProgressBar";
import { cx } from "../common/cx";
import { formatMoney, formatNumber } from "../common/format";
import { ContractCard } from "../contracts/ContractCard";
import { UrgentBanner } from "../contracts/UrgentBanner";
import styles from "./Dashboard.module.css";


export function Dashboard(): JSX.Element {
  const { i18n } = useLingui();
  const t = useLocalize();
  const player = useGameStore((state) => state.player);
  const offers = useGameStore((state) => state.offers);
  const refreshOffersNow = useGameStore((state) => state.refreshOffersNow);
  const unlockedClients = useGameStore((state) => state.unlockedClients);
  const resultHistory = useGameStore((state) => state.resultHistory);
  const setActiveContract = useGameStore((state) => state.setActiveContract);
  const navigateTo = useGameStore((state) => state.navigateTo);
  const viewHistoryResult = useGameStore((state) => state.viewHistoryResult);

  useEffect(() => {
    analytics.dashboardViewed();
  }, []);

  const openContract = (id: ContractId): void => {
    const contract = getContractById(id);
    analytics.contractOpened({ contractId: id, clientId: contract?.clientId ?? "" });
    setActiveContract(id);
    navigateTo("contract_details");
  };

  const recommended = useMemo(() => getRecommendedContract(contracts, player), [player]);
  const recommendedClient = recommended ? getClientById(recommended.clientId) : undefined;

  const nextUnlock = useMemo(
    () => getNextUnlockClient(clients, unlockedClients),
    [unlockedClients],
  );

  const nextUnlockRep = nextUnlock?.requiredReputation ?? 0;
  const urgent =
    recommended && offers.urgent && isUrgentActive(offers, recommended.id, Date.now())
      ? offers.urgent
      : null;

  return (
    <div className={styles.grid}>
      <div className={styles.main}>
        <Panel title={<Trans>Рекомендуем</Trans>} icon={<IconStar size={18} />}>
          {recommended ? (
            <div data-tutorial="recommended-contract" className={styles.recommended}>
              {urgent && (
                <UrgentBanner
                  deadlineMs={urgent.deadlineMs}
                  durationMs={urgent.durationMs}
                  bonusPct={urgent.bonusPct}
                  onExpire={refreshOffersNow}
                />
              )}
              <ContractCard
                contract={recommended}
                client={recommendedClient}
                unlocked
                completed={false}
                reputation={player.reputation}
                recommended
                onOpen={() => openContract(recommended.id)}
              />
            </div>
          ) : (
            <EmptyState
              icon={<IconInbox size={24} />}
              title={<Trans>Нет доступных контрактов</Trans>}
              description={<Trans>Подними репутацию, чтобы открыть новые задания.</Trans>}
              action={
                <Button variant="secondary" onClick={() => navigateTo("contracts")}>
                  <Trans>К контрактам</Trans>
                </Button>
              }
            />
          )}
        </Panel>

        <Panel title={<Trans>Недавние результаты</Trans>}>
          {resultHistory.length > 0 ? (
            <ul className={styles.recentList}>
              {resultHistory.slice(0, 5).map((entry, index) => {
                const entryContract = getContractById(entry.contractId);
                return (
                  <li key={`${entry.contractId}-${entry.at}-${index}`} className={styles.recentItem}>
                    <button
                      type="button"
                      className={styles.recent}
                      onClick={() => viewHistoryResult(entry)}
                    >
                      <GradeSeal grade={entry.grade} score={entry.score} />
                      <div className={styles.recentInfo}>
                        <span className={styles.recentTitle}>
                          {entryContract ? t(entryContract.title) : null}
                        </span>
                        <span className={styles.recentRewards}>
                          <span className={cx(styles.reward, styles.money, "mono")}>
                            +{formatMoney(entry.money)}
                          </span>
                          <span className={cx(styles.reward, "mono")}>
                            +{formatNumber(entry.xp)} <Trans>XP</Trans>
                          </span>
                        </span>
                      </div>
                      <span className={cx(styles.recentDate, "mono")}>
                        {new Date(entry.at).toLocaleDateString()}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyState
              icon={<IconInbox size={24} />}
              title={<Trans>Пока нет завершённых контрактов</Trans>}
              description={<Trans>Возьми первый контракт - результат появится здесь.</Trans>}
            />
          )}
        </Panel>
      </div>

      <div className={styles.side}>
        {nextUnlock && (
          <Panel title={<Trans>Скоро откроется</Trans>} icon={<IconRocket size={18} />}>
            <div className={styles.unlock}>
              <span className={styles.unlockTitle}>{t(nextUnlock.name)}</span>
              <span className={styles.unlockCond}>
                <Trans>Откроется при репутации {nextUnlockRep}</Trans>
              </span>
            </div>
            <ProgressBar
              value={player.reputation}
              max={nextUnlockRep || 1}
              context="accent"
              label={i18n._(msg`Репутация`)}
            />
          </Panel>
        )}

        <Panel title={<Trans>Достижения</Trans>} icon={<IconAward size={18} />}>
          <AchievementsPanel earnedIds={player.achievements} compact />
        </Panel>
      </div>
    </div>
  );
}
